import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts";
import { format, subDays, startOfMonth, endOfMonth, isSameMonth } from "date-fns";

// Custom tooltip component to ensure correct colors
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium text-gray-900">{`Day ${label}`}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {`${entry.dataKey === 'currentMonth' ? 'Current Month' : 'Previous Month'}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Constant colors for the charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

interface MonthlyTrendData {
  month: string;
  revenue: number;
  patients: number;
}

interface DailyPatientData {
  day: string;
  currentMonth: number;
  previousMonth: number;
}

interface MonthlyPatientsChartProps {
  monthlyTrend: MonthlyTrendData[];
  dailyPatientData: DailyPatientData[];
}

export function MonthlyPatientsChart({ monthlyTrend, dailyPatientData }: MonthlyPatientsChartProps) {
  const daysInMonth = dailyPatientData.length;
  
  // Check if there's data to display
  if (!monthlyTrend || monthlyTrend.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">👥</div>
        <h3 className="text-lg font-semibold mb-2">No Monthly Patient Data Available</h3>
        <p className="text-muted-foreground">
          Monthly patient trends will appear here once you have completed patient consultations.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary and Analysis - Compact Row Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Patient Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Patient Summary</CardTitle>
            <CardDescription className="text-sm">Key metrics from daily patient data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-xl font-bold text-green-600">
                  {dailyPatientData.reduce((sum, item) => sum + item.currentMonth, 0)}
                </div>
                <div className="text-xs text-muted-foreground">Current Month</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-xl font-bold text-blue-600">
                  {dailyPatientData.reduce((sum, item) => sum + item.previousMonth, 0)}
                </div>
                <div className="text-xs text-muted-foreground">Previous Month</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-xl font-bold text-purple-600">
                  {(() => {
                    const currentTotal = dailyPatientData.reduce((sum, item) => sum + item.currentMonth, 0);
                    const previousTotal = dailyPatientData.reduce((sum, item) => sum + item.previousMonth, 0);
                    const growth = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : 0;
                    return `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`;
                  })()}
                </div>
                <div className="text-xs text-muted-foreground">Growth</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Growth Analysis */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Growth Analysis</CardTitle>
            <CardDescription className="text-sm">Patient growth trends and insights</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dailyPatientData.length >= 2 && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <div className="text-sm font-semibold text-yellow-700">Growth Rate</div>
                    <div className="text-xl font-bold text-yellow-600">
                      {(() => {
                        const currentTotal = dailyPatientData.reduce((sum, item) => sum + item.currentMonth, 0);
                        const previousTotal = dailyPatientData.reduce((sum, item) => sum + item.previousMonth, 0);
                        const growth = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : 0;
                        return `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`;
                      })()}
                    </div>
                    <div className="text-xs text-muted-foreground">vs previous month</div>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <div className="text-sm font-semibold text-orange-700">Peak Day</div>
                    <div className="text-xl font-bold text-orange-600">
                      {dailyPatientData.reduce((max, item) => 
                        item.currentMonth > max.currentMonth ? item : max
                      ).day}
                    </div>
                    <div className="text-xs text-muted-foreground">Highest count</div>
                  </div>
                </div>
              )}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-semibold text-gray-700">Trend Analysis</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {dailyPatientData.length >= 3 ? (
                    <div>
                      <p>• Tracked over {dailyPatientData.length} days</p>
                      <p>• Avg daily: {Math.round(dailyPatientData.reduce((sum, item) => sum + item.currentMonth, 0) / dailyPatientData.length)} patients</p>
                      <p>• Total: {dailyPatientData.reduce((sum, item) => sum + item.currentMonth, 0)} patients</p>
                    </div>
                  ) : (
                    <p>More data needed for trend analysis.</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Patient Charts - Compact Row Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly Patient Trend Line Chart */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Monthly Patient Trend</CardTitle>
            <CardDescription className="text-sm">Current vs Previous month comparison</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyPatientData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="day" 
                    interval={2}
                    fontSize={12}
                  />
                  <YAxis fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="currentMonth" 
                    stroke="#82ca9d" 
                    strokeWidth={2}
                    name="Current Month" 
                    dot={{ fill: '#82ca9d', strokeWidth: 1, r: 3 }}
                    activeDot={{ r: 4, stroke: '#82ca9d', strokeWidth: 1, fill: '#fff' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="previousMonth" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                    name="Previous Month" 
                    dot={{ fill: '#8884d8', strokeWidth: 1, r: 3 }}
                    activeDot={{ r: 4, stroke: '#8884d8', strokeWidth: 1, fill: '#fff' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Daily Patient Bar Chart */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Daily Patient Bar Chart</CardTitle>
            <CardDescription className="text-sm">Color-coded daily patient counts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyPatientData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="day" 
                    interval={2}
                    fontSize={12}
                  />
                  <YAxis fontSize={12} />
                  <Tooltip 
                    formatter={(value: number) => [value, 'Daily Patients']}
                    labelFormatter={(label) => `Day ${label}`}
                  />
                  <Legend />
                  <Bar 
                    dataKey="currentMonth" 
                    name="Daily Patients"
                    radius={[3, 3, 0, 0]}
                  >
                    {dailyPatientData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % COLORS.length]} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 